/* eslint-disable */
import {
  Badge,
  Box,
  ChakraProvider,
  Code,
  Divider,
  Flex,
  Grid,
  Heading,
  Icon,
  IconButton,
  Image,
  Stack,
  Text,
  Tooltip,
  useColorModeValue,
  useToast,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import {
  MdSend,
  MdPictureAsPdf,
  MdMail,
  MdDraw,
  MdPhotoCamera,
  MdSave,
  MdAttachFile,
  MdOutlineTransferWithinAStation,
  MdAccessTime,
  MdCheckCircle,
  MdPeople,
  MdFingerprint,
  MdFilterList,
  MdRefresh,
  MdPersonOff,
  MdShare,
  MdCarRental,
  MdLibraryAdd,
  MdInfoOutline,
  MdFormatPaint,
  MdSearch,
  MdApps,
  MdBusiness,
  MdVerifiedUser,
} from 'react-icons/md';
import * as React from 'react';
import initialTheme from 'theme/theme';

/* ─── Types ─── */
type TagType = 'feature' | 'mejora' | 'fix';

interface FeatureItem {
  icon: any;
  iconColor: string;
  iconBg: string;
  title: string;
  desc: string;
}

interface StatusPill {
  label: string;
  scheme: string;
}

interface FileChip {
  name: string;
  kind: 'new' | 'mod';
  layer: 'fe' | 'be' | 'db';
}

interface Entry {
  date: string;
  tags: TagType[];
  title: string;
  desc: string;
  featured?: boolean;
  features?: FeatureItem[];
  statusFlow?: StatusPill[];
  statusNote?: string;
  files?: FileChip[];
  bullets?: string[];
  /** Identificador estable para compartir un enlace directo a esta tarjeta (#slug). Si no se define, se genera a partir del título. */
  slug?: string;
}

const slugify = (text: string): string =>
  text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

/* ─── Data ─── */
const TAG_META: Record<TagType, { label: string; colorScheme: string }> = {
  feature: { label: 'Nuevo',   colorScheme: 'green' },
  mejora:  { label: 'Mejora',  colorScheme: 'blue'  },
  fix:     { label: 'Corrección', colorScheme: 'orange' },
};

const LAYER_COLOR: Record<string, string> = { fe: 'teal', be: 'blue', db: 'purple' };

const ENTRIES: Entry[] = [
  {
    date: '18 Sep 2026',
    tags: ['feature'],
    title: 'Permisos por módulo y departamento, y dos roles administrativos nuevos',
    desc: 'Ya se puede acotar exactamente qué páginas ve un usuario y qué departamentos adicionales puede consultar, sin depender solo de su rol base. Además, dos roles nuevos permiten delegar administración sin dar acceso total al sistema.',
    featured: true,
    features: [
      {
        icon: MdApps,
        iconColor: 'teal.300',
        iconBg: 'teal.900',
        title: 'Módulos por usuario',
        desc: 'A un usuario se le pueden asignar módulos específicos (Activos, Resguardos, Salidas, Facturas, etc.) desde su ficha. Sin asignaciones explícitas, sigue viendo todo igual que antes.',
      },
      {
        icon: MdBusiness,
        iconColor: 'blue.300',
        iconBg: 'blue.900',
        title: 'Departamentos adicionales',
        desc: 'Un usuario puede ver más de un departamento, no solo el suyo, marcando exactamente cuáles desde su ficha.',
      },
      {
        icon: MdVerifiedUser,
        iconColor: 'purple.300',
        iconBg: 'purple.900',
        title: 'Admin Regional y Admin de Departamento',
        desc: 'Dos roles nuevos: uno con acceso a todo su hotel, otro acotado a su departamento pero con permisos para administrar usuarios ahí dentro. Ninguno puede crear un usuario con más poder del que él mismo tiene.',
      },
    ],
    bullets: [
      'Bloqueo real por URL directa a páginas restringidas, no solo ocultas del menú',
      'Si un usuario no tiene acceso al Dashboard, ya no ve una pantalla vacía: se le lleva al primer módulo al que sí tenga acceso',
      'Al seleccionar un rol para un usuario, se muestra qué puede hacer ese rol específicamente',
    ],
    files: [
      { name: 'permissions.ts',          kind: 'mod', layer: 'fe' },
      { name: 'ModuleProtectedRoute.tsx', kind: 'mod', layer: 'fe' },
      { name: 'configUsuarios.tsx',      kind: 'mod', layer: 'fe' },
      { name: 'usuarios.controller.js',  kind: 'mod', layer: 'be' },
      { name: '013_add_orden_a_modulos.sql', kind: 'new', layer: 'db' },
    ],
  },
  {
    date: '18 Sep 2026',
    tags: ['fix'],
    title: 'Autorización de resguardos: ya no se salta la firma del colaborador',
    desc: 'Cuando un Supervisor o Administrador autorizaba un resguardo directo (sin mandarlo a revisión), el sistema lo marcaba como recibido sin que la persona que recibe los activos llegara a firmar nunca.',
    featured: true,
    features: [
      {
        icon: MdVerifiedUser,
        iconColor: 'purple.300',
        iconBg: 'purple.900',
        title: 'Autorizar ya no es lo mismo que "recibido"',
        desc: 'Autorizar ahora solo pasa el resguardo a estado "Autorizado". La firma real del colaborador (presencial o remota) sigue siendo un paso aparte, como debía ser desde un inicio.',
      },
      {
        icon: MdDraw,
        iconColor: 'teal.300',
        iconBg: 'teal.900',
        title: 'Reutiliza tu firma ya registrada',
        desc: 'Al autorizar, si ya tienes una firma guardada se usa automáticamente en vez de pedirte dibujarla cada vez.',
      },
      {
        icon: MdCheckCircle,
        iconColor: 'green.300',
        iconBg: 'green.900',
        title: 'El estado se actualiza al momento',
        desc: 'Se corrigió que la tabla no reflejara "Recepción confirmada" de inmediato al firmar.',
      },
    ],
    bullets: [
      'Se corrigió que el PDF del resguardo mostrara "no asignado" en vez del nombre real de quien recibe los activos',
      'La tarjeta de autorización ahora aparece justo después del resumen del resguardo, no hasta el final del panel',
    ],
    files: [
      { name: 'resguardos.controller.js', kind: 'mod', layer: 'be' },
      { name: 'mail.service.js',          kind: 'mod', layer: 'be' },
      { name: 'inventario.routes.js',     kind: 'mod', layer: 'be' },
      { name: 'ReferralsTable.tsx',       kind: 'mod', layer: 'fe' },
    ],
  },
  {
    date: '18 Sep 2026',
    tags: ['fix', 'mejora'],
    title: 'Editar usuarios, y correcciones en su jefe/supervisor asignado',
    desc: 'Ahora sí se puede editar un usuario ya existente (antes solo se podía dar de alta), y se corrigieron dos errores relacionados con las asignaciones de un usuario.',
    bullets: [
      'Cambiar el jefe/supervisor de un colaborador ya se aplica correctamente al editar — antes se quedaba con el anterior',
      'Corregido un error ("Duplicate entry") que podía impedir crear un usuario con hoteles, módulos o departamentos asignados',
    ],
    files: [
      { name: 'configUsuarios.tsx', kind: 'mod', layer: 'fe' },
      { name: 'usuarios.controller.js', kind: 'mod', layer: 'be' },
      { name: 'db_insert.js', kind: 'mod', layer: 'be' },
    ],
  },
  {
    date: '14 Sep 2026',
    tags: ['feature'],
    title: 'Importación masiva de activos por CSV',
    desc: 'Nueva pantalla "Importar Activos" para dar de alta muchos activos a la vez desde un archivo CSV, en vez de capturarlos uno por uno.',
    featured: true,
    bullets: [
      'Vista previa editable antes de guardar: se pueden corregir celdas directamente en la tabla si algo viene mal en el archivo',
      'Resuelve automáticamente Departamento, Línea/Sublínea, Marca, Modelo, Proveedor, Estado, Tipo de Adquisición y Rubro de Depreciación por nombre',
      'El folio se genera solo si no viene en el archivo, respetando el consecutivo real de cada hotel y línea',
      'Genera el código QR real de cada activo importado',
      'Todo o nada: si alguna fila tiene un error, no se importa ninguna hasta corregirlo',
    ],
    files: [
      { name: 'ImportarActivos.tsx',    kind: 'new', layer: 'fe' },
      { name: 'importar.controller.js', kind: 'mod', layer: 'be' },
      { name: 'db_insert.js',           kind: 'mod', layer: 'be' },
    ],
  },
  {
    date: '14 Sep 2026',
    tags: ['feature'],
    title: 'Depreciación de activos',
    desc: 'El sistema ahora calcula la depreciación de cada activo automáticamente, con un catálogo configurable de porcentajes por rubro y por año.',
    featured: true,
    bullets: [
      'Nueva página de Configuración con la Tabla de Depreciación: porcentaje anual por rubro (Edificio, Equipo de cómputo, Mobiliario, etc.), versionada por año',
      'El % que se aplica es el de la tabla del año en que se adquirió el activo, no el del año actual',
      'Cálculo automático de depreciación mensual, depreciación acumulada y valor en libros',
      'En Detalles del activo se ve una barra visual roja con el avance de la depreciación desde la fecha de adquisición hasta hoy',
      'El valor en libros nunca baja de $0, aunque el activo lleve más tiempo en uso del que tardó en depreciarse por completo',
      'El campo "Rubro de depreciación" es opcional al crear un activo',
    ],
    files: [
      { name: 'configDepreciacion.tsx', kind: 'new', layer: 'fe' },
      { name: 'depreciacion.tsx',       kind: 'new', layer: 'fe' },
      { name: 'newProduct/index.tsx',   kind: 'mod', layer: 'fe' },
      { name: 'pageProduct/index.tsx',  kind: 'mod', layer: 'fe' },
      { name: 'depreciacion.controller.js', kind: 'new', layer: 'be' },
      { name: 'db_insert.js',          kind: 'mod', layer: 'be' },
      { name: '008_create_tbaTablaDepreciacion.sql', kind: 'new', layer: 'db' },
    ],
  },
  {
    date: '14 Sep 2026',
    tags: ['feature'],
    title: 'Clasificación Capex / OPEX / Arrendamiento',
    desc: 'Cada activo puede clasificarse por su tipo de adquisición, y la lista de activos ya se puede filtrar por esa clasificación.',
    bullets: [
      'Nuevo campo "Tipo de Adquisición" (Capex, OPEX o Arrendamiento) en Crear y Editar Activo',
      'Filtro por tipo de adquisición en la lista general de activos',
      'Editable desde el detalle del activo mientras no esté asignado a un colaborador',
    ],
    files: [
      { name: 'tipoAdquisicion.tsx',      kind: 'new', layer: 'fe' },
      { name: 'SearchTableOrders.tsx',    kind: 'mod', layer: 'fe' },
      { name: 'tipoadquisicion.controller.js', kind: 'new', layer: 'be' },
      { name: '007_create_tbaTipoAdquisicion.sql', kind: 'new', layer: 'db' },
    ],
  },
  {
    date: '14 Sep 2026',
    tags: ['mejora'],
    title: 'Nuevos campos: ubicación exacta y fecha de adquisición',
    desc: 'Se agregaron dos campos que antes no existían para los activos, disponibles en creación, edición e importación por CSV.',
    bullets: [
      '"Ubicación exacta" — texto libre para precisar dónde está el activo dentro del hotel',
      '"Fecha de adquisición" — con un selector de calendario real en vez de un input de texto',
      'La fecha de adquisición es la que determina qué año de la Tabla de Depreciación aplica',
    ],
    files: [
      { name: 'DateField.tsx',         kind: 'new', layer: 'fe' },
      { name: 'newProduct/index.tsx',  kind: 'mod', layer: 'fe' },
      { name: 'pageProduct/index.tsx', kind: 'mod', layer: 'fe' },
      { name: '009_add_depreciacion_ubicacion_fechaadquisicion_a_activos.sql', kind: 'new', layer: 'db' },
    ],
  },
  {
    date: '14 Sep 2026',
    tags: ['feature'],
    title: 'Historial de Bajas',
    desc: 'Nueva pantalla para ver todos los activos que se han dado de baja definitivamente y descargar su PDF de respaldo, igual que ya existía para Salidas.',
    featured: true,
    bullets: [
      'Lista agrupada por solicitud de baja: fecha, responsable, motivo y activos incluidos',
      'Botón para descargar el PDF oficial de cada baja',
      'Se corrigió un bug donde el PDF se guardaba en una ruta que no existía en el servidor',
      'El hotel se toma del selector global del menú, sin un selector duplicado dentro de la pantalla',
    ],
    files: [
      { name: 'HistorialBajas.tsx',      kind: 'new', layer: 'fe' },
      { name: 'inventario.routes.js',    kind: 'mod', layer: 'be' },
    ],
  },
  {
    date: '14 Sep 2026',
    tags: ['mejora'],
    title: 'Dashboard: conteo y detalle de bajas',
    desc: 'El tablero principal ahora también muestra información sobre los activos dados de baja.',
    bullets: [
      'Nuevo indicador "Bajas Registradas" en la fila de KPIs',
      'Nueva tarjeta "Bajas Recientes" con las últimas bajas: fecha, motivo, responsable y cantidad de activos',
    ],
    files: [
      { name: 'dashboards/index.tsx', kind: 'mod', layer: 'fe' },
    ],
  },
  {
    date: '14 Sep 2026',
    tags: ['mejora'],
    title: 'Selects, calendario y formato de moneda en Crear/Editar Activo',
    desc: 'Se homogeneizó la forma en que se capturan varios campos del formulario de activos.',
    bullets: [
      'Proveedor, Factura, Modelo y Tipo de Adquisición migrados de un select nativo a un buscador con react-select',
      'Selector de fecha tipo calendario para Fecha de Adquisición y Garantía',
      'El campo "Costo" se escribe y se muestra en formato de pesos mexicanos, con separador de miles en vivo',
      'El bloque de "Folio sugerido" se movió justo debajo de "Permitido Salir" para que sea más visible',
    ],
    files: [
      { name: 'CurrencyField.tsx',    kind: 'new', layer: 'fe' },
      { name: 'newProduct/index.tsx', kind: 'mod', layer: 'fe' },
    ],
  },
  {
    date: '14 Sep 2026',
    tags: ['fix', 'mejora'],
    title: 'Configuración de Usuarios restringida a administradores',
    desc: 'La pantalla para crear y administrar usuarios del sistema ahora es exclusiva para el rol Administrador; antes cualquier rol salvo el de solo-lectura podía entrar y crear usuarios.',
    bullets: [
      'El menú "Configuración > Usuarios" ya no aparece si el usuario no es Administrador',
      'Entrar directo por la URL también queda bloqueado para quien no sea Administrador',
      'De paso se corrigió que en Editar Activo el campo "Departamento" mostraba todos los departamentos sin filtrar, en vez de solo el del usuario (salvo que su rol tenga acceso a todos)',
    ],
    files: [
      { name: 'permissions.ts',        kind: 'mod', layer: 'fe' },
      { name: 'configUsuarios.tsx',    kind: 'new', layer: 'fe' },
      { name: 'routes.tsx',            kind: 'mod', layer: 'fe' },
      { name: 'Sidebar.tsx',           kind: 'mod', layer: 'fe' },
      { name: 'pageProduct/index.tsx', kind: 'mod', layer: 'fe' },
    ],
  },
  {
    date: '19 Ago 2026',
    tags: ['feature'],
    title: 'Firma en estado Autorizado: elige remota o presencial, y agrega copia + mensaje',
    desc: 'Al autorizar un resguardo, ahora se pregunta con claridad cómo se va a firmar. Antes convivían en la misma pantalla el enlace remoto y la firma en el momento, sin quedar claro cuál acción correspondía a cuál.',
    featured: true,
    features: [
      {
        icon: MdSend,
        iconColor: 'teal.300',
        iconBg: 'teal.900',
        title: 'Elige el modo de firma',
        desc: 'Dos tarjetas claras para escoger: "Firma remota" (se envía un enlace al correo del colaborador) o "Firma presencial" (se firma ahora mismo en este dispositivo). Solo aparecen los campos y el botón del modo elegido.',
      },
      {
        icon: MdMail,
        iconColor: 'blue.300',
        iconBg: 'blue.900',
        title: 'Copia a otros correos (opcional)',
        desc: 'Al enviar el enlace de firma remota, puedes marcar la casilla "Enviar copia a otros correos" y agregar varios destinatarios separados por comas.',
      },
      {
        icon: MdDraw,
        iconColor: 'green.300',
        iconBg: 'green.900',
        title: 'Mensaje personalizado (opcional)',
        desc: 'Puedes escribir un mensaje que se incluye en el correo del enlace de firma, además del contenido habitual del aviso.',
      },
    ],
    files: [
      { name: 'ReferralsTable.tsx',    kind: 'mod', layer: 'fe' },
      { name: 'resguardos.tsx',        kind: 'mod', layer: 'fe' },
      { name: 'inventario.routes.js',  kind: 'mod', layer: 'be' },
      { name: 'mail.service.js',       kind: 'mod', layer: 'be' },
    ],
  },
  {
    date: '19 Ago 2026',
    tags: ['mejora'],
    title: 'Búsqueda por folio y número de serie en Resguardos',
    desc: 'El buscador de la tabla de resguardos ahora también encuentra resultados por el folio y el número de serie de los activos asociados, no solo por el nombre del activo o del colaborador.',
    featured: true,
    features: [
      {
        icon: MdSearch,
        iconColor: 'teal.300',
        iconBg: 'teal.900',
        title: 'Folio y número de serie ya son buscables',
        desc: 'Escribir un folio o número de serie (con o sin guiones y espacios) encuentra el resguardo correspondiente, igual que ya funcionaba con el nombre del activo.',
      },
      {
        icon: MdCheckCircle,
        iconColor: 'green.300',
        iconBg: 'green.900',
        title: 'También funciona en resguardos cancelados',
        desc: 'El historial de resguardos cancelados conserva el folio y la serie de los activos que tenía al momento de cancelarse, así la búsqueda encuentra lo mismo ahí.',
      },
    ],
    files: [
      { name: 'ReferralsTable.tsx', kind: 'mod', layer: 'fe' },
      { name: 'db_insert.js',       kind: 'mod', layer: 'be' },
    ],
  },
  {
    date: '20 Jul 2026',
    tags: ['fix'],
    title: 'Logo correcto en el PDF de resguardo según el hotel',
    desc: 'El PDF de resguardo dejaba el logo en blanco para los hoteles que no fueran Grand Velas, porque la imagen se traía desde un sitio externo (funjet.com) que bloqueaba la petición.',
    bullets: [
      'El logo ya no depende de un sitio externo; ahora se toma del propio CDN de Velas Resorts',
      'Grand Velas (cualquier destino) sigue mostrando su logo correspondiente',
      'Velas Vallarta y Contact Center ya muestran su logo específico',
      'El resto de los hoteles (Casa Velas, Mar del Cabo, etc.) usa el logo genérico de Velas Resorts',
    ],
    files: [
      { name: 'mail.service.js', kind: 'mod', layer: 'be' },
    ],
  },
  {
    date: '20 Jul 2026',
    tags: ['mejora'],
    title: 'Buscador para agregar activos a un resguardo',
    desc: 'En el detalle de un resguardo en Borrador, agregar un activo nuevo ya no depende de desplazarte por un selector con toda la lista: ahora puedes escribir para encontrarlo al instante.',
    featured: true,
    features: [
      {
        icon: MdSearch,
        iconColor: 'teal.300',
        iconBg: 'teal.900',
        title: 'Búsqueda por nombre, folio, serie o modelo',
        desc: 'El listado de activos disponibles para agregar se filtra en vivo según lo que escribas, reemplazando el selector desplegable que antes había que recorrer entero.',
      },
      {
        icon: MdCheckCircle,
        iconColor: 'green.300',
        iconBg: 'green.900',
        title: 'Mejor rendimiento con catálogos grandes',
        desc: 'Se limitó cuántos resultados se pintan a la vez y se corrigió un problema que hacía la lista más lenta mientras más activos había disponibles para agregar.',
      },
    ],
    files: [
      { name: 'ReferralsTable.tsx', kind: 'mod', layer: 'fe' },
    ],
  },
  {
    date: '20 Jul 2026',
    tags: ['mejora'],
    title: 'Buscador y alta rápida de departamento en el resguardo',
    desc: 'Al editar el departamento de un colaborador (local, foráneo o externo) en el formulario de resguardo, ahora puedes buscarlo por nombre o darlo de alta si no está en la lista.',
    bullets: [
      'Campo de búsqueda arriba del listado de departamentos para encontrarlo más rápido',
      'Si el departamento escrito no existe en la lista, aparece la opción de agregarlo tal cual se escribió',
      'El botón "Confirmar" queda deshabilitado hasta que haya un departamento seleccionado o escrito',
    ],
    files: [
      { name: 'Details.tsx', kind: 'mod', layer: 'fe' },
    ],
  },
  {
    date: '20 Jul 2026',
    tags: ['feature'],
    title: 'Activos en arrendamiento',
    desc: 'Un activo ya puede marcarse como arrendado y vincularse a la empresa arrendadora correspondiente, con un catálogo que se puede completar sobre la marcha sin salir del formulario.',
    featured: true,
    features: [
      {
        icon: MdCarRental,
        iconColor: 'orange.300',
        iconBg: 'orange.900',
        title: 'Toggle "Arrendamiento" en Nuevo Activo',
        desc: 'Al activarlo se vuelve obligatorio elegir la arrendadora dueña del equipo antes de generar el QR. Si se desactiva, el campo se limpia automáticamente.',
      },
      {
        icon: MdLibraryAdd,
        iconColor: 'teal.300',
        iconBg: 'teal.900',
        title: 'Catálogo de arrendadoras con alta al vuelo',
        desc: 'Si la arrendadora no existe en la lista, se puede escribir su nombre y crearla ahí mismo; queda disponible de inmediato para ese activo y para el resto del catálogo.',
      },
      {
        icon: MdCheckCircle,
        iconColor: 'green.300',
        iconBg: 'green.900',
        title: 'Visible en el detalle del activo',
        desc: 'La página de detalle ahora muestra un badge "Arrendado" con el nombre de la arrendadora, o "Activo propio" cuando no aplica.',
      },
    ],
    files: [
      { name: 'SelectField.tsx',        kind: 'mod', layer: 'fe' },
      { name: 'arrendadoras.tsx',       kind: 'new', layer: 'fe' },
      { name: 'newProduct/index.tsx',   kind: 'mod', layer: 'fe' },
      { name: 'pageProduct/index.tsx',  kind: 'mod', layer: 'fe' },
      { name: 'arrendadoras.controller.js', kind: 'new', layer: 'be' },
      { name: 'inventario.routes.js',   kind: 'mod', layer: 'be' },
      { name: 'db_insert.js',           kind: 'mod', layer: 'be' },
      { name: '001_create_tbaArrendadoras.sql',    kind: 'new', layer: 'db' },
      { name: '002_add_arrendadora_to_activos.sql', kind: 'new', layer: 'db' },
    ],
  },
  {
    date: '20 Jul 2026',
    tags: ['mejora'],
    title: 'Diseño unificado en el formulario de Nuevo Activo',
    desc: 'Se homogeneizó la apariencia de todos los campos del formulario — antes convivían selects con fondo y bordes distintos junto a inputs con otro estilo, y ahora todos comparten el mismo lenguaje visual.',
    featured: true,
    features: [
      {
        icon: MdFormatPaint,
        iconColor: 'purple.300',
        iconBg: 'purple.900',
        title: 'Mismo estilo para todos los campos',
        desc: 'Línea, Sublínea, Marca, Modelo, Departamento y Estado ya no se ven como cajas ajenas al resto del formulario: comparten bordes, colores y radio con los campos de texto, incluyendo modo oscuro.',
      },
      {
        icon: MdInfoOutline,
        iconColor: 'orange.300',
        iconBg: 'orange.900',
        title: 'Aviso flotante al cambiar de Departamento',
        desc: 'Si seleccionas un departamento distinto al tuyo, aparece un aviso recordando que el activo quedará asignado a ese departamento. Se cierra solo a los 10 segundos o con la "x".',
      },
    ],
    files: [
      { name: 'SelectField.tsx',      kind: 'new', layer: 'fe' },
      { name: 'newProduct/index.tsx', kind: 'mod', layer: 'fe' },
    ],
  },
  {
    date: '20 Jul 2026',
    tags: ['fix'],
    title: 'Aviso de resguardo pendiente con datos del solicitante más confiables',
    desc: 'El correo que le avisa al supervisor que tiene un resguardo pendiente de firma podía llegar con el nombre, departamento o puesto del solicitante en blanco si la sesión de quien lo envió a revisión ya se había cerrado.',
    bullets: [
      'El nombre, departamento y puesto del solicitante ahora se guardan en el resguardo desde el momento en que se crea',
      'El correo de aviso al supervisor toma esos datos del resguardo, sin depender de que el navegador de quien lo envió siga con la sesión abierta',
    ],
  },
  {
    date: '17 Jul 2026',
    tags: ['feature'],
    title: 'Aviso de resguardos con colaboradores dados de baja',
    desc: 'La campana de notificaciones ahora también avisa cuando un colaborador ya dado de baja en nómina todavía conserva un resguardo activo, para poder darle seguimiento sin tener que revisar hotel por hotel.',
    featured: true,
    features: [
      {
        icon: MdPersonOff,
        iconColor: 'red.300',
        iconBg: 'red.900',
        title: 'Nuevo aviso en la campana de notificaciones',
        desc: 'Se consulta periódicamente el listado de colaboradores dados de baja que aún tienen un resguardo a su nombre en el hotel activo, y se suman al contador de la campana junto con las notificaciones existentes.',
      },
      {
        icon: MdFilterList,
        iconColor: 'teal.300',
        iconBg: 'teal.900',
        title: '"Ver más detalles" filtra por número de colaborador',
        desc: 'Al presionar un aviso, se abre la tabla de Resguardos filtrada por el número de colaborador del dueño del resguardo, evitando coincidencias falsas que ocurrían al buscar por número de resguardo.',
      },
    ],
    files: [
      { name: 'resguardosBaja.context.tsx', kind: 'new', layer: 'fe' },
      { name: 'NavbarLinksAdmin.tsx',        kind: 'mod', layer: 'fe' },
      { name: 'ReferralsTable.tsx',          kind: 'mod', layer: 'fe' },
      { name: 'resguardos.tsx',              kind: 'mod', layer: 'fe' },
    ],
  },
  {
    date: '30 Jun 2026',
    tags: ['mejora', 'fix'],
    title: 'Resguardos: departamento del dueño y loader al abrir',
    desc: 'Se corrigió la visualización del departamento del colaborador en la tabla de resguardos y se agregó un indicador de carga al consultar los activos de un resguardo.',
    featured: true,
    features: [
      {
        icon: MdPeople,
        iconColor: 'teal.300',
        iconBg: 'teal.900',
        title: 'Columna "Departamento del Dueño" ahora muestra el dato correcto',
        desc: 'Se corrigió un error en la carga inicial de la tabla de resguardos que impedía mostrar el departamento del colaborador (del sistema Abrhil). El dato ya aparece correctamente en la columna "Departamento del Dueño" y en el detalle del drawer.',
      },
      {
        icon: MdAccessTime,
        iconColor: 'blue.300',
        iconBg: 'blue.900',
        title: 'Loader al consultar los activos del resguardo',
        desc: 'Al presionar "Ver" en un resguardo, la tabla de activos ahora muestra un spinner mientras se obtienen los datos del servidor, evitando confusión cuando la carga tarda unos segundos.',
      },
    ],
    files: [
      { name: 'ReferralsTable.tsx', kind: 'mod', layer: 'fe' },
    ],
  },
  {
    date: '29 Jun 2026',
    tags: ['mejora', 'fix'],
    title: 'Lista de activos: columnas Ubicación y Depto. Origen',
    desc: 'La tabla de activos ahora distingue entre dónde está físicamente un activo (según el resguardo) y el departamento al que pertenece originalmente.',
    featured: true,
    features: [
      {
        icon: MdFilterList,
        iconColor: 'teal.300',
        iconBg: 'teal.900',
        title: 'Nueva columna "Ubicación"',
        desc: 'Muestra el departamento actual del activo según el resguardo vigente. Si el activo fue asignado a otro departamento, aquí se verá reflejado.',
      },
      {
        icon: MdCheckCircle,
        iconColor: 'blue.300',
        iconBg: 'blue.900',
        title: 'Nueva columna "Depto. Origen"',
        desc: 'Muestra el departamento con el que fue registrado originalmente el activo, independientemente de dónde esté asignado actualmente.',
      },
    ],
    files: [
      { name: 'SearchTableOrders.tsx', kind: 'mod', layer: 'fe' },
      { name: 'db_insert.js',          kind: 'mod', layer: 'be' },
    ],
  },
  {
    date: '29 Jun 2026',
    tags: ['feature', 'mejora'],
    title: 'Visibilidad de firmas remotas pendientes',
    desc: 'Ahora puedes ver de un vistazo qué colaboradores aún no han firmado su resguardo de forma remota, tanto en el dashboard como en la tabla de resguardos.',
    featured: true,
    features: [
      {
        icon: MdFilterList,
        iconColor: 'yellow.300',
        iconBg: 'yellow.900',
        title: 'Filtro "Firma Remota" en la tabla de resguardos',
        desc: 'Se agregó el botón de filtro "Firma Remota" en la barra de estados. Al presionarlo muestra únicamente los resguardos en estado "Pendiente de firma del colaborador". Funciona igual que los demás filtros: un segundo clic lo invierte y un tercero lo limpia.',
      },
      {
        icon: MdFingerprint,
        iconColor: 'teal.300',
        iconBg: 'teal.900',
        title: 'Panel de pendientes al inicio del dashboard',
        desc: 'El dashboard ahora muestra una tarjeta al inicio con la lista de colaboradores que tienen firma remota pendiente, ordenados del más antiguo al más reciente. Cada fila indica el nombre, departamento y fecha en que se envió el enlace.',
      },
      {
        icon: MdCheckCircle,
        iconColor: 'blue.300',
        iconBg: 'blue.900',
        title: 'KPI de firmas pendientes en los indicadores principales',
        desc: 'Se añadió un quinto indicador en la fila superior del dashboard que muestra el total de resguardos con firma remota aún pendiente, junto al resto de métricas clave.',
      },
    ],
  },
  {
    date: '29 Jun 2026',
    tags: ['fix'],
    title: 'Correcciones en el proceso de baja',
    desc: 'Se resolvieron tres problemas que impedían consultar y completar correctamente un proceso de baja desde la lista.',
    featured: true,
    features: [
      {
        icon: MdDraw,
        iconColor: 'orange.300',
        iconBg: 'orange.900',
        title: 'Las firmas ya aparecen al ver el proceso',
        desc: 'Al abrir un proceso de baja existente desde la lista, las firmas de quienes ya habían firmado no se mostraban. Ahora cargan correctamente junto con la imagen de la firma.',
      },
      {
        icon: MdPeople,
        iconColor: 'blue.300',
        iconBg: 'blue.900',
        title: 'Nombres de los firmantes visibles',
        desc: 'Los nombres de cada firmante no aparecían en las tarjetas aunque estuvieran registrados. Ahora se muestran correctamente.',
      },
      {
        icon: MdCheckCircle,
        iconColor: 'green.300',
        iconBg: 'green.900',
        title: 'Botón "Procesar baja definitiva" se habilita',
        desc: 'Aunque todas las firmas estuvieran completas, el botón permanecía deshabilitado. Ya se activa correctamente en cuanto el último firmante autoriza.',
      },
    ],
  },
  {
    date: '29 Jun 2026',
    tags: ['mejora'],
    title: 'Tarjetas de firma con más información',
    desc: 'Las tarjetas de cada firmante en el proceso de baja ahora muestran su estado de forma más clara y completa.',
    bullets: [
      'Badge de estado en cada tarjeta: "Firmado" en verde, "Tu turno" en azul o "Pendiente" en gris',
      'Nombre del firmante más visible; muestra "Sin asignar" cuando no hay nombre registrado',
      'Fecha en que se realizó la firma, cuando el servidor la devuelve',
      'La imagen de la firma aparece automáticamente para quienes ya firmaron, sin mostrar el lienzo en blanco',
    ],
  },
  {
    date: '29 Jun 2026',
    tags: ['mejora'],
    title: 'Módulo de salidas unificado con motivos predefinidos',
    desc: 'El flujo de salidas se simplificó en una sola pantalla. Ahora eliges dentro del formulario si la salida es temporal (préstamo) o definitiva (transferencia), y el motivo se selecciona de opciones fijas en lugar de escribirlo a mano.',
    featured: true,
    features: [
      {
        icon: MdOutlineTransferWithinAStation,
        iconColor: 'blue.300',
        iconBg: 'blue.900',
        title: 'Un solo botón de salida',
        desc: 'Los botones "Salida" y "Salida temporal" se fusionaron en uno. La barra de acciones ahora tiene solo "Salida" y el tipo se elige dentro del formulario.',
      },
      {
        icon: MdAccessTime,
        iconColor: 'orange.300',
        iconBg: 'orange.900',
        title: 'Tipo de salida: Temporal o Definitiva',
        desc: 'Un selector al inicio del formulario define si es Temporal (préstamo con destinatario y fecha de devolución) o Definitiva (transferencia a otro hotel). Los campos del formulario cambian según el tipo elegido.',
      },
      {
        icon: MdCheckCircle,
        iconColor: 'green.300',
        iconBg: 'green.900',
        title: 'Motivos predefinidos con opción libre',
        desc: 'En lugar de un campo de texto abierto, ahora hay cinco botones de selección: Venta, Reparación, Lavado, Obsequio y Otro. Al elegir "Otro" aparece un campo para describir el caso específico.',
      },
    ],
  },
  {
    date: '29 Jun 2026',
    tags: ['mejora'],
    title: 'Nuevo estado: Fuera Propiedad',
    desc: 'Se agregó el estado "Fuera Propiedad" para rastrear activos que han salido de las instalaciones del hotel.',
    bullets: [
      'Los activos con estado fuera_propiedad ahora se muestran con el badge "FUERA PROPIEDAD" en color morado',
      'El estado se mapea correctamente desde el API y aparece en la tabla de activos',
    ],
  },
  {
    date: '29 Jun 2026',
    tags: ['feature'],
    title: 'Evidencia fotográfica en bajas de activos',
    desc: 'El proceso de baja ahora requiere adjuntar fotos del activo como evidencia obligatoria. Las fotos se guardan en el servidor, aparecen en el PDF oficial y se adjuntan al correo del siguiente firmante.',
    featured: true,
    features: [
      {
        icon: MdPhotoCamera,
        iconColor: 'red.300',
        iconBg: 'red.900',
        title: 'Tomar foto o elegir de galería',
        desc: 'En el paso de motivos, el encargado puede tomar una foto directamente con la cámara del dispositivo o seleccionarla de la galería. Se puede adjuntar más de una imagen.',
      },
      {
        icon: MdSave,
        iconColor: 'teal.300',
        iconBg: 'teal.900',
        title: 'Las fotos se guardan en el servidor',
        desc: 'Al firmar el primer firmante, las fotos se escriben como archivos en el servidor. Así no se pierden aunque se cierre el navegador entre firmas.',
      },
      {
        icon: MdAttachFile,
        iconColor: 'blue.300',
        iconBg: 'blue.900',
        title: 'Adjuntas al correo del siguiente firmante',
        desc: 'Cada vez que se notifica al siguiente firmante, las fotos viajan como archivos adjuntos en el correo para que pueda ver el estado del activo antes de firmar.',
      },
      {
        icon: MdPictureAsPdf,
        iconColor: 'purple.300',
        iconBg: 'purple.900',
        title: 'Incluidas en el PDF de baja definitiva',
        desc: 'El documento oficial generado al completar todas las firmas incluye una sección con las fotografías de evidencia del activo dado de baja.',
      },
    ],
  },
  {
    date: '29 Jun 2026',
    tags: ['mejora'],
    title: 'Filtros combinados y exportación a Excel',
    desc: 'Ahora puedes filtrar la tabla de activos por varios estados o departamentos al mismo tiempo y descargar exactamente lo que ves en pantalla.',
    bullets: [
      'Selecciona varios estados o departamentos a la vez para afinar tu búsqueda',
      'El número de filtros activos se muestra en cada campo para que siempre sepas qué tienes aplicado',
      'El botón "Exportar" descarga solo los activos que están visibles en ese momento, con la fecha del día en el nombre del archivo',
    ],
  },
  {
    date: '29 Jun 2026',
    tags: ['mejora'],
    title: 'Colores de estado más claros en activos',
    desc: 'Los indicadores de estado de cada activo ahora son más fáciles de leer de un vistazo.',
    bullets: [
      'Baja aparece en rojo',
      'Inactivo aparece en naranja',
      'Bodega aparece en teal',
    ],
  },
  {
    date: '29 Jun 2026',
    tags: ['feature'],
    title: 'Firma de resguardo a distancia',
    desc: 'Ya no es necesario que el colaborador esté frente a la pantalla para firmar su resguardo. El encargado le envía un enlace por correo y el colaborador firma desde donde esté.',
    featured: true,
    features: [
      {
        icon: MdSend,
        iconColor: 'teal.300',
        iconBg: 'teal.900',
        title: 'Enviar enlace de firma',
        desc: 'Desde el detalle del resguardo autorizado, el encargado escribe el correo del colaborador y pulsa "Enviar enlace de firma".',
      },
      {
        icon: MdMail,
        iconColor: 'blue.300',
        iconBg: 'blue.900',
        title: 'El colaborador recibe un correo',
        desc: 'El colaborador recibe un mensaje con un botón para revisar sus activos y firmar. El enlace es válido por 72 horas.',
      },
      {
        icon: MdDraw,
        iconColor: 'green.300',
        iconBg: 'green.900',
        title: 'Firma desde cualquier dispositivo',
        desc: 'Se abre una página sencilla donde el colaborador ve la lista de activos que recibirá y puede firmar con el dedo o el mouse.',
      },
      {
        icon: MdPictureAsPdf,
        iconColor: 'purple.300',
        iconBg: 'purple.900',
        title: 'Documento enviado automáticamente',
        desc: 'Al confirmar la firma, el colaborador recibe por correo el documento oficial del resguardo con todas las firmas.',
      },
      {
        icon: MdRefresh,
        iconColor: 'orange.300',
        iconBg: 'orange.900',
        title: 'Reenviar el enlace si el correo estaba mal',
        desc: 'Si el enlace se envió a un correo incorrecto, puedes abrir el resguardo, escribir el correo correcto y reenviar. El enlace anterior queda inválido y se genera uno nuevo al instante.',
      },
    ],
    statusFlow: [
      { label: 'Autorizado',           scheme: 'purple' },
      { label: 'Pendiente de firma',   scheme: 'yellow' },
      { label: 'Recepción confirmada', scheme: 'green'  },
    ],
    statusNote: 'Si el colaborador firma en persona, el resguardo pasa directamente a "Recepción confirmada" sin pasar por el estado intermedio.',
  },
  {
    date: '30 Jun 2026',
    tags: ['mejora'],
    title: 'Reenviar enlace de firma si el correo estaba mal',
    desc: 'Si el enlace de firma remota se envió a un correo incorrecto, ahora puedes corregirlo y reenviar el enlace sin cancelar el resguardo ni volver a empezar.',
    bullets: [
      'Abre el detalle del resguardo en estado "Pendiente de firma del colaborador"',
      'Verás el correo al que se envió el enlace anterior destacado en amarillo',
      'Escribe el correo correcto en el campo y pulsa "Reenviar enlace"',
      'El enlace anterior queda inválido automáticamente y se genera uno nuevo',
      'Si dejas el campo vacío, el enlace se reenvía al mismo correo',
    ],
  },
  {
    date: '29 Jun 2026',
    tags: ['mejora'],
    title: 'Cambiar el estado de un activo desde su detalle',
    desc: 'Ahora puedes marcar un activo como Activo, Inactivo o En Bodega directamente desde su página de detalle, sin necesidad de pasos adicionales.',
    bullets: [
      'Selector visible solo cuando el activo está disponible para cambio manual',
      'Para activos en procesos especiales (asignados, en baja, salida) el selector no aparece para evitar confusiones',
      'Si el activo tiene un responsable asignado, se te pedirá confirmación antes de cambiar su estado',
    ],
  },
  {
    date: 'Jun 2026',
    tags: ['mejora'],
    title: 'Las fotos de activos cargan más rápido',
    desc: 'Las imágenes que se suben a los activos ahora se optimizan automáticamente, así las páginas cargan más rápido sin que tengas que hacer nada diferente.',
    bullets: [
      'Las fotos se comprimen al subirlas — el proceso es el mismo de siempre',
      'Las listas y detalles de activos se ven más ágiles',
    ],
  },
  {
    date: 'Jun 2026',
    tags: ['feature'],
    title: 'Firma digital en salidas y bajas de activos',
    desc: 'Los procesos de salida temporal y baja de activos ahora requieren firmas digitales de todos los involucrados, generando un documento oficial al finalizar.',
    bullets: [
      'Salida temporal — firman el custodio, el jefe inmediato y el contralor',
      'Baja de activo — se genera un documento firmado y el historial queda guardado',
      'Los equipos que salen de la propiedad quedan marcados como "Fuera de propiedad" para poder rastrearlos',
    ],
  },
  {
    date: 'May 2026',
    tags: ['fix'],
    title: 'Correcciones generales',
    desc: 'Se resolvieron varios problemas menores reportados por usuarios.',
    bullets: [
      'Los documentos PDF se generaban con errores en algunos casos — ya está corregido',
      'Al quitar un activo de un resguardo, su estado no siempre se actualizaba correctamente',
      'Los resguardos cancelados no aparecían en el historial — ahora sí se muestran',
      'El mensaje de confirmación al cancelar un resguardo no aparecía en todos los casos',
    ],
  },
  {
    date: 'Abr 2026',
    tags: ['feature'],
    title: 'Cancelar resguardos y ver su historial',
    desc: 'Un resguardo ya confirmado puede cancelarse si los activos deben regresar. Los equipos quedan disponibles de nuevo y el registro se guarda para consultas futuras.',
    bullets: [
      'Los activos del resguardo quedan libres inmediatamente al cancelarlo',
      'El resguardo cancelado se conserva en el historial — no desaparece',
      'Puedes filtrar y ver todos los resguardos cancelados desde la tabla principal',
      'Se te pide confirmación antes de cancelar para evitar errores accidentales',
    ],
  },
];

/* ─── Sub-components ─── */

function EntryTag({ type }: { type: TagType }) {
  const { label, colorScheme } = TAG_META[type];
  return (
    <Badge colorScheme={colorScheme} borderRadius="full" px="2.5" py="0.5" fontSize="10px" fontWeight="700" letterSpacing=".05em" textTransform="uppercase">
      {label}
    </Badge>
  );
}

function StatusFlow({ pills, note }: { pills: StatusPill[]; note?: string }) {
  const bg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const border = useColorModeValue('gray.200', 'whiteAlpha.100');
  return (
    <Box mt="3" mb="1">
      <Flex align="center" gap="2" flexWrap="wrap" bg={bg} border="1px solid" borderColor={border} borderRadius="md" px="3" py="2.5" rowGap="2">
        {pills.map((p, i) => (
          <React.Fragment key={i}>
            <Badge colorScheme={p.scheme} borderRadius="full" px="2.5" py="1" fontSize="11px" fontWeight="600">
              {p.label}
            </Badge>
            {i < pills.length - 1 && (
              <Text fontSize="12px" color="gray.400">→</Text>
            )}
          </React.Fragment>
        ))}
      </Flex>
      {note && (
        <Text fontSize="12px" color="gray.500" mt="1.5" fontStyle="italic">{note}</Text>
      )}
    </Box>
  );
}

function FileChips({ files }: { files: FileChip[] }) {
  return (
    <Wrap spacing="2" mt="4">
      {files.map((f, i) => (
        <WrapItem key={i}>
          <Flex align="center" gap="1.5" bg={useColorModeValue('gray.100', 'whiteAlpha.50')} border="1px solid" borderColor={useColorModeValue('gray.200', 'whiteAlpha.100')} borderRadius="md" px="2" py="1">
            <Badge colorScheme={f.kind === 'new' ? 'green' : 'blue'} fontSize="8px" px="1.5" py="0.5" borderRadius="sm" textTransform="uppercase" letterSpacing=".06em">
              {f.kind}
            </Badge>
            <Badge colorScheme={LAYER_COLOR[f.layer]} fontSize="8px" px="1.5" py="0.5" borderRadius="sm" textTransform="uppercase" letterSpacing=".06em">
              {f.layer}
            </Badge>
            <Code fontSize="11px" bg="transparent" color={useColorModeValue('gray.700', 'gray.200')} px="0">
              {f.name}
            </Code>
          </Flex>
        </WrapItem>
      ))}
    </Wrap>
  );
}

function ShareButton({ slug, label = 'Copiar enlace de esta tarjeta', description = 'Ya puedes compartir el link directo a esta actualización.' }: { slug: string; label?: string; description?: string }) {
  const toast = useToast();
  const hoverBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const iconColor = useColorModeValue('gray.400', 'gray.500');

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}${window.location.pathname}#${slug}`;
    window.history.replaceState(null, '', `#${slug}`);
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      window.prompt('Copia el enlace:', url);
    }
    toast({
      title: 'Enlace copiado',
      description,
      status: 'success',
      duration: 2500,
      isClosable: true,
      position: 'bottom-right',
    });
  };

  return (
    <Tooltip label={label} fontSize="xs" hasArrow>
      <IconButton
        aria-label="Compartir"
        icon={<Icon as={MdShare} boxSize="14px" />}
        size="xs"
        variant="ghost"
        color={iconColor}
        _hover={{ bg: hoverBg, color: 'teal.400' }}
        onClick={handleShare}
        ms="auto"
      />
    </Tooltip>
  );
}

function FeaturedEntry({ entry, slug, highlighted, showDate = true }: { entry: Entry; slug: string; highlighted?: boolean; showDate?: boolean }) {
  const cardBg   = useColorModeValue('white', 'navy.800');
  const border   = useColorModeValue('gray.200', 'whiteAlpha.100');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const featureBg = useColorModeValue('gray.50', 'whiteAlpha.50');

  return (
    <Box
      id={slug}
      bg={cardBg}
      border="1px solid"
      borderColor={highlighted ? 'teal.400' : border}
      borderTop="3px solid"
      borderTopColor="teal.400"
      borderRadius="xl"
      p={{ base: '5', md: '8' }}
      mb="2"
      boxShadow={highlighted ? '0 0 0 3px var(--chakra-colors-teal-200)' : 'none'}
      transition="box-shadow .6s ease, border-color .6s ease"
      scrollMarginTop="70px"
    >
      <Flex gap="2" align="center" mb="3" flexWrap="wrap">
        {showDate && <Text fontSize="12px" fontFamily="mono" color={mutedColor}>{entry.date}</Text>}
        {entry.tags.map(t => <EntryTag key={t} type={t} />)}
        <ShareButton slug={slug} />
      </Flex>

      <Heading size="md" color={useColorModeValue('navy.700', 'white')} mb="2" fontWeight="700" letterSpacing="-.015em">
        {entry.title}
      </Heading>
      <Text fontSize="14.5px" color={mutedColor} mb="5" maxW="560px">{entry.desc}</Text>

      {entry.features && (
        <Stack spacing="3" mb="5">
          {entry.features.map((f, i) => (
            <Flex key={i} align="flex-start" gap="3" bg={featureBg} borderRadius="lg" p="3">
              <Flex w="28px" h="28px" borderRadius="md" bg={f.iconBg} align="center" justify="center" flexShrink={0} mt="0.5">
                <Icon as={f.icon} color={f.iconColor} boxSize="14px" />
              </Flex>
              <Box>
                <Text fontSize="13.5px" fontWeight="600" color={textColor}>{f.title}</Text>
                <Text fontSize="12.5px" color={mutedColor}>{f.desc}</Text>
              </Box>
            </Flex>
          ))}
        </Stack>
      )}

      {entry.statusFlow && <StatusFlow pills={entry.statusFlow} note={entry.statusNote} />}
      {entry.files && <FileChips files={entry.files} />}
    </Box>
  );
}

function CompactEntry({ entry, slug, highlighted, showDate = true }: { entry: Entry; slug: string; highlighted?: boolean; showDate?: boolean }) {
  const textColor  = useColorModeValue('navy.700', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const border     = useColorModeValue('gray.100', 'whiteAlpha.100');

  return (
    <Box
      id={slug}
      borderTop="1px solid"
      borderColor={border}
      pt="7"
      pb="7"
      px={highlighted ? '4' : '0'}
      mx={highlighted ? '-4' : '0'}
      borderRadius={highlighted ? 'lg' : '0'}
      boxShadow={highlighted ? '0 0 0 3px var(--chakra-colors-teal-200)' : 'none'}
      transition="box-shadow .6s ease, padding .3s ease"
      scrollMarginTop="70px"
    >
      <Flex gap="2" align="center" mb="2" flexWrap="wrap">
        {showDate && <Text fontSize="12px" fontFamily="mono" color={mutedColor}>{entry.date}</Text>}
        {entry.tags.map(t => <EntryTag key={t} type={t} />)}
        <ShareButton slug={slug} />
      </Flex>
      <Heading size="sm" color={textColor} mb={entry.desc ? "2" : "3"} fontWeight="650" letterSpacing="-.01em">
        {entry.title}
      </Heading>
      {entry.desc && (
        <Text fontSize="13.5px" color={mutedColor} mb="3">{entry.desc}</Text>
      )}
      {entry.bullets && (
        <Stack spacing="1.5">
          {entry.bullets.map((b, i) => (
            <Flex key={i} align="flex-start" gap="2">
              <Box w="5px" h="5px" borderRadius="full" bg={mutedColor} flexShrink={0} mt="8px" />
              <Text fontSize="13.5px" color={mutedColor}>{b}</Text>
            </Flex>
          ))}
        </Stack>
      )}
    </Box>
  );
}

/* ─── Sidebar ─── */
function Sidebar() {
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const labelColor = useColorModeValue('gray.400', 'gray.500');

  const SideLabel = ({ children }: { children: React.ReactNode }) => (
    <Text fontSize="10px" fontFamily="mono" letterSpacing=".12em" textTransform="uppercase" color={labelColor} mb="2">{children}</Text>
  );
  const SideLink = ({ children, active }: { children: React.ReactNode; active?: boolean }) => (
    <Text fontSize="13px" color={active ? 'teal.400' : mutedColor} borderLeft="2px solid" borderLeftColor={active ? 'teal.400' : 'transparent'} pl="3" py="1" cursor="pointer" _hover={{ color: 'teal.300' }} transition="color .15s">
      {children}
    </Text>
  );

  return (
    <Box pt="14" display={{ base: 'none', lg: 'block' }}>
      <Stack spacing="6">
        <Box>
          <SideLabel>Versiones</SideLabel>
          <Stack spacing="0">
            <SideLink active>Jun 2026</SideLink>
            <SideLink>May 2026</SideLink>
            <SideLink>Abr 2026</SideLink>
          </Stack>
        </Box>
        <Box>
          <SideLabel>Categorías</SideLabel>
          <Stack spacing="0">
            {(['feature', 'mejora', 'fix'] as TagType[]).map(t => (
              <Flex key={t} align="center" gap="2" py="1" cursor="pointer">
                <Box w="7px" h="7px" borderRadius="full" bg={`${TAG_META[t].colorScheme}.400`} />
                <Text fontSize="12.5px" color={mutedColor} _hover={{ color: 'var(--chakra-colors-teal-300)' }}>{TAG_META[t].label}</Text>
              </Flex>
            ))}
          </Stack>
        </Box>
        <Box>
          <SideLabel>Módulos</SideLabel>
          <Stack spacing="0">
            {['Resguardos', 'Activos', 'Salidas', 'Reportes'].map(m => (
              <SideLink key={m}>{m}</SideLink>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}

/* ─── Page ─── */
export default function Changelog() {
  const headerBg   = useColorModeValue('whiteAlpha.900', 'rgba(11,21,32,.92)');
  const headerBorder = useColorModeValue('gray.200', 'whiteAlpha.100');
  const textColor   = useColorModeValue('navy.700', 'white');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');

  // Slugs estables para poder compartir el link directo a una tarjeta (#slug).
  // Se desambiguan con un sufijo si dos entradas generan el mismo texto.
  const slugs = React.useMemo(() => {
    const seen = new Map<string, number>();
    return ENTRIES.map(entry => {
      const base = entry.slug || slugify(entry.title);
      const count = seen.get(base) || 0;
      seen.set(base, count + 1);
      return count === 0 ? base : `${base}-${count + 1}`;
    });
  }, []);

  // Agrupa las entradas por fecha (aunque no sean contiguas en ENTRIES),
  // preservando el orden de primera aparición de cada fecha.
  const dateGroups = React.useMemo(() => {
    const order: string[] = [];
    const indicesByDate = new Map<string, number[]>();
    ENTRIES.forEach((entry, i) => {
      if (!indicesByDate.has(entry.date)) {
        indicesByDate.set(entry.date, []);
        order.push(entry.date);
      }
      indicesByDate.get(entry.date)!.push(i);
    });
    const seenSlugs = new Map<string, number>();
    return order.map(date => {
      const base = `fecha-${slugify(date)}`;
      const count = seenSlugs.get(base) || 0;
      seenSlugs.set(base, count + 1);
      const slug = count === 0 ? base : `${base}-${count + 1}`;
      return { date, slug, indices: indicesByDate.get(date)! };
    });
  }, []);

  const [highlightedSlug, setHighlightedSlug] = React.useState<string | null>(null);

  React.useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    setHighlightedSlug(hash);
    const el = document.getElementById(hash);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const timeout = setTimeout(() => setHighlightedSlug(null), 3000);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <ChakraProvider theme={initialTheme}>
      {/* Sticky header */}
      <Box
        position="sticky" top="0" zIndex="sticky"
        bg={headerBg}
        backdropFilter="blur(12px)"
        borderBottom="1px solid"
        borderColor={headerBorder}
      >
        <Flex maxW="1060px" mx="auto" px="7" h="52px" align="center" justify="space-between">
          <Flex align="center" gap="2.5">
            <Image
              src="https://resources.velasresorts.com/avance/VR.png"
              alt="Velas Resorts"
              h="28px"
              w="auto"
              objectFit="contain"
            />
            <Text fontSize="13.5px" fontWeight="600" color={textColor}>
              Inventarios <Text as="span" fontWeight="400" color={mutedColor}>· Velas Resorts</Text>
            </Text>
          </Flex>
          <Badge colorScheme="teal" variant="outline" borderRadius="full" px="3" py="0.5" fontSize="10px" letterSpacing=".1em" textTransform="uppercase">
            Changelog
          </Badge>
        </Flex>
      </Box>

      {/* Body */}
      <Grid maxW="1060px" mx="auto" px={{ base: '5', md: '7' }} pb="24" templateColumns={{ base: '1fr', lg: '180px 1fr' }} gap={{ base: '0', lg: '14' }}>
        <Sidebar />

        <Box pt="14">
          {/* Page title */}
          <Box mb="12">
            <Heading size="lg" color={textColor} fontWeight="700" letterSpacing="-.025em" mb="2">
              Features & Changelog
            </Heading>
            <Text fontSize="15px" color={mutedColor}>
              Historial de mejoras del Sistema de Control de Inventarios de Velas Resorts.
            </Text>
          </Box>

          {/* Entries agrupadas por fecha */}
          {dateGroups.map(({ date, slug: dateSlug, indices }) => (
            <Box
              key={dateSlug}
              id={dateSlug}
              mb="10"
              scrollMarginTop="70px"
              borderRadius="lg"
              boxShadow={dateSlug === highlightedSlug ? '0 0 0 3px var(--chakra-colors-teal-200)' : 'none'}
              transition="box-shadow .6s ease"
            >
              <Flex align="center" gap="3" mb="4">
                <Text fontSize="12px" fontFamily="mono" fontWeight="700" letterSpacing=".04em" textTransform="uppercase" color={mutedColor} whiteSpace="nowrap">
                  {date}
                </Text>
                <Box flex="1" h="1px" bg={headerBorder} />
                <ShareButton
                  slug={dateSlug}
                  label="Copiar enlace de esta fecha"
                  description="Ya puedes compartir el link directo a todas las actualizaciones de esta fecha."
                />
              </Flex>
              {indices.map(i => {
                const entry = ENTRIES[i];
                return entry.featured
                  ? <FeaturedEntry key={i} entry={entry} slug={slugs[i]} highlighted={slugs[i] === highlightedSlug} showDate={false} />
                  : <CompactEntry key={i} entry={entry} slug={slugs[i]} highlighted={slugs[i] === highlightedSlug} showDate={false} />;
              })}
            </Box>
          ))}

          {/* Footer */}
          <Divider mt="12" mb="6" borderColor={headerBorder} />
          <Flex justify="space-between" flexWrap="wrap" gap="2">
            <Text fontSize="12px" color={mutedColor}>Sistema de Control de Inventarios · Velas Resorts</Text>
            <Text fontSize="11px" fontFamily="mono" color={mutedColor}>rama-experimental-453</Text>
          </Flex>
        </Box>
      </Grid>
    </ChakraProvider>
  );
}
